import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { authedFetch } from './platformTenants';

export const RoleSchema = z.enum(['tenant-admin', 'warehouse-admin', 'voice-supervisor', 'voice-operator']);
export type Role = z.infer<typeof RoleSchema>;

export const MembershipSchema = z.object({
  id: z.number(),
  role: RoleSchema,
  warehouse_id: z.number().nullable(),
  warehouse_code: z.string().nullable(),
  warehouse_name: z.string().nullable(),
  is_default: z.boolean(),
  assigned_at: z.string(),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const AdminUserSchema = z.object({
  id: z.number(),
  email: z.string().nullable(),
  display_name: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  memberships: z.array(MembershipSchema),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUsersListSchema = z.object({
  data: z.array(AdminUserSchema),
  cursor: z.string().nullable().optional(),
});

function tenantHeaders(tenantCode: string | null, extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { ...(extra ?? {}) };
  if (tenantCode) h['X-Tenant-Id'] = tenantCode;
  return h;
}

export function useTenantAdminUsers(token: string | null, tenantCode: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', tenantCode],
    queryFn: async () => AdminUsersListSchema.parse(
      await authedFetch('/admin/users', token, { headers: tenantHeaders(tenantCode) }),
    ),
    enabled: token !== null && tenantCode !== null,
  });
}

export function useTenantAdminUser(token: string | null, tenantCode: string | null, userId: number | null) {
  return useQuery({
    queryKey: ['admin', 'users', tenantCode, userId],
    queryFn: async () => AdminUserSchema.parse(
      await authedFetch(`/admin/users/${userId}`, token, { headers: tenantHeaders(tenantCode) }),
    ),
    enabled: token !== null && tenantCode !== null && userId !== null,
  });
}

export function useCreateAdminUser(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      body: {
        email: string; display_name: string; password_temp: string;
        role: Role; warehouse_id?: number | null; accept_existing?: boolean;
      };
      idempotencyKey: string;
    }) =>
      await authedFetch('/admin/users', token, {
        method: 'POST',
        body: JSON.stringify(vars.body),
        headers: tenantHeaders(tenantCode, { 'Idempotency-Key': vars.idempotencyKey }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDisableUser(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) =>
      await authedFetch(`/admin/users/${userId}/disable`, token, {
        method: 'POST',
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useEnableUser(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) =>
      await authedFetch(`/admin/users/${userId}/enable`, token, {
        method: 'POST',
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useResetPassword(token: string | null, tenantCode: string | null) {
  return useMutation({
    mutationFn: async (vars: { userId: number; temporary_password: string }) =>
      await authedFetch(`/admin/users/${vars.userId}/reset-password`, token, {
        method: 'POST',
        body: JSON.stringify({ password_temp: vars.temporary_password }),
        headers: tenantHeaders(tenantCode),
      }),
    // intentionally NO invalidation — pure side-effect
  });
}
